const scriptCache = {};

export const environment = {
  env: 'development',
  isProd() {
    return this.env === 'production';
  }
};

/**
  * Logs a message to the console, when appropraite.
  */
export function log() {
  if (console && !environment.isProd()) {
    console.log.apply(console, arguments);
  }
}

/**
  * If the provided argument is a function, call it.
  * Useful for invoking callback queues.
  */
export function runCallback(cb, data) {
  if (typeof cb === 'function') cb(data);
}

/**
  * Loads a script source; unique source URLs will only be requested once.
  * Repeat requests for a script will have their callbacks queued on the pending request,
  * and will all be fulfilled when the original script request is loaded.
  * Loaded scripts will fire a new request callback on the next event loop.
  * @param {String} src URL to load.
  * @param {Function} callback function to invoke when source is loaded.
  * @param {Function} errorCallback function to invoke if source fails to load.
  */
export function getScript(src, callback, errorCallback) {
  if (!src) {
    throw new Error('src is required');
  }

  // NOT YET REQUESTED.
  // Create request queue and start loading script.
  if (!scriptCache.hasOwnProperty(src)) {
    scriptCache[src] = [{ callback, errorCallback }];
    var script = document.createElement('script');
    script.async = 1;
    var scriptPrev = document.getElementsByTagName('script')[0];
    if (scriptPrev) {
      scriptPrev.parentNode.insertBefore(script, scriptPrev);
    } else {
      document.body.appendChild(script);
    }

    script.onerror = function() {
      scriptCache[src].forEach(({ errorCallback }) => runCallback(errorCallback));
      // `false` cached value indicates that the script failed to load
      scriptCache[src] = false;
    };

    script.onload = script.onreadystatechange = function(evt, isAbort) {
      if (isAbort || !script.readyState || /loaded|complete/.test(script.readyState)) {
        script.onload = script.onreadystatechange = null;
        if (!isAbort) {
          scriptCache[src].forEach(({ callback }) => runCallback(callback));
          // `true` cached value indicates that script loaded successfully
          scriptCache[src] = true;
        } else {
          delete scriptCache[src];
        }
      }
    };

    script.src = src;
  } else if (Array.isArray(scriptCache[src])) {
    // REQUESTED, WITH UNFULFILLED CALLBACK QUEUE.
    // Add the callback into the queue.
    scriptCache[src].push({ callback, errorCallback });
  } else if (scriptCache[src]) {
    // COMPLETE.
    // Just run the callback on the next event loop.
    // Using setTimeout assures this callback still runs async.
    setTimeout(() => runCallback(callback), 0);
  } else {
    // FAILED.
    // Just run the callback on the next event loop.
    // Using setTimeout assures this callback still runs async.
    setTimeout(() => runCallback(errorCallback), 0);
  }
}

/**
  * Extends a base object with properties from additional passed objects.
  * @param {Object} base object to extend.
  * @param {...Object} objects to copy properties from.
  * @returns {Object} the base object, extended by all other argument objects.
  */
export function extend(base) {
  for (var i = 1; i < arguments.length; i++) {
    var ext = arguments[i];
    for (var key in ext) {
      if (ext.hasOwnProperty(key)) base[key] = ext[key];
    }
  }
  return base;
}

// Formats a two-position decimal value.
export function formatDecimal(num) {
  return Math.round(num * 100) / 100;
}

// Formats a time string as `12:07:00`
export function timestamp() {
  const date = new Date();
  let hour = date.getHours();
  let mins = date.getMinutes();
  let secs = date.getSeconds();
  hour = ('00' + hour).substr(-2, 2);
  mins = ('00' + mins).substr(-2, 2);
  secs = ('00' + secs).substr(-2, 2);
  return `${hour}:${mins}:${secs}`;
}
